import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth, USER_TYPE_LABELS } from "@/context/AuthContext";
import { Badge, FileLinks, TopicMultiSelect, ExpertiseEditor, expertisePayload, toEditableExpertise } from "@/components/common";
import { toast } from "sonner";
import { MapPin, ShieldAlert, KeyRound, Pencil } from "lucide-react";

export default function Profile() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  useEffect(() => { if (id) api.get(`/users/${id}`).then((r) => setP(r.data)); else setP(user); }, [id, user]);
  if (!p) return <p className="text-muted-foreground">{t("Loading…")}</p>;
  const own = !id || id === user.id;

  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-page">
      <div className="border-b-2 border-foreground pb-3">
        <p className="overline mb-1">{t(USER_TYPE_LABELS[p.user_type])}</p>
        <div className="flex items-center gap-2"><h1 className="font-display text-3xl font-extrabold tracking-tight">{p.name}</h1><Badge tone={p.verification_status === "approved" ? "green" : p.verification_status === "pending" ? "amber" : "red"}>{t(p.verification_status)}</Badge></div>
      </div>

      <div className="border border-border bg-white divide-y divide-border">
        <Row label={t("Location")} value={<span className="flex items-center gap-1"><MapPin size={14} /> {p.location?.city || "—"}, {p.location?.region} {p.location?.country}{p.location?.eu ? " · EU" : ""}</span>} />
        <Row label={t("Interests")} value={(p.topics_interest || []).map((x) => t(x)).join(", ") || "—"} />
        {own && p.role && p.role !== "user" && <Row label={t("Role")} value={<Badge tone="purple">{t(p.role.replace("_", " "))}</Badge>} />}
        {own && typeof p.violation_count === "number" && <Row label={t("Standing")} value={p.banned ? <Badge tone="red">{t("banned")}</Badge> : p.suspended_until ? <Badge tone="amber">{t("suspended")}</Badge> : <Badge tone="green">{t("good standing")}</Badge>} />}
      </div>

      <div>
        <h2 className="font-display text-xl font-bold tracking-tight mb-3">{t("Expertise")}</h2>
        <div className="space-y-3">
          {(p.expertise || []).map((e, i) => (
            <div key={i} className="border border-border bg-white p-5" data-testid="expertise-item">
              <div className="flex items-center gap-2 mb-1"><h3 className="font-semibold">{e.area}</h3><Badge tone={e.status === "verified" ? "green" : e.status === "pending" ? "amber" : "red"}>{t(e.status)}</Badge></div>
              <p className="text-sm text-muted-foreground">{e.description}</p>
              {(e.evidence_links || []).map((l, j) => <a key={j} href={l} target="_blank" rel="noreferrer" className="text-xs text-primary underline mr-2">{t("link")} {j + 1}</a>)}
              <FileLinks ids={e.evidence_files} />
            </div>
          ))}
          {(p.expertise || []).length === 0 && <p className="text-sm text-muted-foreground">{t("No expertise declared.")}</p>}
        </div>
      </div>

      {own && (user.banned || user.suspended_until) && (
        <div className="border border-amber-300 bg-amber-50 p-4 flex items-start gap-2 text-sm text-amber-900 rounded-sm"><ShieldAlert size={18} className="shrink-0 mt-0.5" /> {t("Behaviour reviews below an average of 5 escalate: warning → next-round suspension → 30-day suspension → permanent ban. Participate respectfully and cite evidence.")}</div>
      )}

      {own && <EditInterestsExpertise user={p} />}

      {own && <ChangePassword />}
    </div>
  );
}

function EditInterestsExpertise({ user }) {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [areas, setAreas] = useState([]);
  const [interests, setInterests] = useState(user.topics_interest || []);
  const [expertise, setExpertise] = useState(toEditableExpertise(user.expertise));
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/policy-areas").then((r) => setAreas(r.data.areas)); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/auth/profile", { topics_interest: interests, expertise: expertisePayload(expertise) });
      await refresh();
      toast.success(t("Profile updated"));
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="edit-interests-expertise">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2"><Pencil size={18} /> {t("Interests & expertise")}</h2>
        {!open && <button onClick={() => setOpen(true)} data-testid="edit-profile-btn" className="text-sm font-semibold text-primary border border-primary px-3 py-1.5 rounded-sm hover:bg-primary hover:text-white transition-colors">{t("Edit")}</button>}
      </div>
      {open && (
        <div className="border border-border bg-white p-5 space-y-4">
          <div>
            <p className="overline mb-2">{t("Topics / Policy areas of interest")}</p>
            <TopicMultiSelect options={areas} value={interests} onChange={setInterests} testid="edit-topics" />
          </div>
          <ExpertiseEditor options={areas} items={expertise} setItems={setExpertise} testid="edit-expertise" />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} data-testid="save-profile-btn" className="bg-primary text-white px-5 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">{busy ? t("Saving…") : t("Save changes")}</button>
            <button onClick={() => setOpen(false)} className="border border-border px-5 py-2 rounded-sm text-sm font-semibold hover:bg-muted">{t("Cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangePassword() {
  const { t } = useTranslation();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error(t("New passwords do not match"));
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success(t("Password updated"));
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="change-password">
      <h2 className="font-display text-xl font-bold tracking-tight mb-3 flex items-center gap-2"><KeyRound size={18} /> {t("Change password")}</h2>
      <form onSubmit={submit} className="border border-border bg-white p-5 space-y-3 max-w-md">
        <input type="password" required placeholder={t("Current password")} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={pic} data-testid="cp-current" />
        <input type="password" required minLength={6} placeholder={t("New password (min 6)")} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={pic} data-testid="cp-new" />
        <input type="password" required placeholder={t("Confirm new password")} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={pic} data-testid="cp-confirm" />
        <button disabled={busy} data-testid="cp-submit" className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">{busy ? t("Updating…") : t("Update password")}</button>
      </form>
    </div>
  );
}
const pic = "w-full border border-border bg-background rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary";
function Row({ label, value }) { return <div className="flex items-center justify-between gap-4 p-4"><span className="overline">{label}</span><span className="text-sm text-foreground text-right">{value}</span></div>; }
